import { ContentLayout } from "@/components/common/content-layout";
import { useHasAccess } from "@/components/common/has-access";
import VaulDrawer from "@/components/ui/drawer";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import usePaymentPages from "@/features/payment-pages/hooks/use-payment-pages";
import { useDeletePaymentPage } from "@/features/payment-pages/hooks/use-delete-payment-page";
import PaymentPageForm from "@/features/payment-pages/ui/payment-page-form";
import PaymentPageCard from "@/features/payment-pages/ui/payment-page-card";
import PaymentPageFilter, {
  PaymentPageFilters,
} from "@/features/payment-pages/ui/payment-page-filter";
import { useMemo, useState } from "react";
import { FileText, XIcon } from "lucide-react";
import { PaymentPage } from "./types/payment-page";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SearchBar } from "@/components/common/search-bar";

export default function PaymentPages() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const page = parseInt(searchParams.get("page") || "0", 10);
  const status = searchParams.get("status") || "ALL";
  const search = searchParams.get("search") || "";

  const currentFilters = useMemo(() => ({ status }), [status]);

  const statusParam = status !== "ALL" ? status : undefined;

  const { data, isLoading, isFetching, error } = usePaymentPages(
    page,
    statusParam,
    search,
  );
  const deleteMutation = useDeletePaymentPage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // The drawer is how a payment page is VIEWED, not only edited, so it must
  // open for everyone who can see the list. Only what it lets you do depends
  // on Edit.
  const canWrite = useHasAccess("write");
  const [selected, setSelected] = useState<PaymentPage | null>(null);

  const paymentPages = useMemo(() => data?.paymentPages || [], [data]);
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  const handlePageChange = (nextPage: number) => {
    if (nextPage >= 0 && nextPage < lastPage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", nextPage.toString());
      navigate(`?${newParams.toString()}`, { replace: true });
    }
  };

  const handleApplyFilters = (filters: PaymentPageFilters) => {
    const newParams = new URLSearchParams();
    newParams.set("page", "0");

    const searchVal = searchParams.get("search");
    if (searchVal?.trim()) {
      newParams.set("search", searchVal.trim());
    }

    if (filters.status && filters.status !== "ALL") {
      newParams.set("status", filters.status);
    }

    setSearchParams(newParams);
  };

  return (
    <ContentLayout
      header={
        <div className="flex flex-wrap items-center gap-4">
          <PaymentPageFilter
            onApplyFilters={handleApplyFilters}
            currentFilters={currentFilters}
          />
          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search payment pages..."
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />,
              }}
            />
          </div>
        </div>
      }
      data={paymentPages}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load payment pages"
      emptyState={{
        message: "No payment pages found",
        icon: <FileText className="h-8 w-8 text-muted-foreground" />,
      }}
    >
      <PaymentPageCard
        paymentPages={paymentPages}
        setShowDrawer={setDrawerOpen}
        setSelected={setSelected}
        onDelete={(id) => deleteMutation.mutate(id)}
        onAdd={() => navigate("new")}
      />
      {paymentPages.length > 0 && (
        <CustomPaginationComponent
          currentPage={currentPage}
          lastPage={lastPage}
          onPageChange={handlePageChange}
        />
      )}

      <VaulDrawer
        drawerDirection="right"
        isOpen={drawerOpen}
        onOpenChange={setDrawerOpen}
        triggerChildren={null}
        contentChildren={
          <PaymentPageForm
            readOnly={!canWrite}
            editMode={Boolean(selected)}
            initialData={selected}
            onClose={() => setDrawerOpen(false)}
          />
        }
      />
    </ContentLayout>
  );
}
